import { useCallback, useEffect, useRef, useState } from "react";
import { RotateCcw, Volume2, VolumeX, Play } from "lucide-react";
import { cancelSpeech, playItemAudio, unlockAudio } from "@/lib/speech";

export const Waveform = ({ active }) => (
  <div className="flex h-8 items-end gap-1" aria-hidden>
    {[0, 1, 2, 3, 4, 5, 6].map((i) => (
      <span key={i} className={`w-1.5 rounded-full bg-indigo-500 ${active ? "animate-wave" : "h-1.5 opacity-40"}`} style={{ animationDelay: `${i * 90}ms` }} />
    ))}
  </div>
);

export const StatusBadge = ({ phase }) => {
  const map = { listening: ["Playing prompt…", "bg-indigo-100 text-indigo-700"], failed: ["Audio blocked", "bg-amber-100 text-amber-800"], answer: ["Speak / answer now", "bg-emerald-100 text-emerald-700"], read: ["Reading", "bg-slate-100 text-slate-700"], write: ["Writing", "bg-emerald-100 text-emerald-700"] };
  const [t, c] = map[phase] || map.answer;
  return <span data-testid="item-status-badge" className={`rounded-full px-3 py-1 text-xs font-semibold ${c}`}>{t}</span>;
};

const GRACE = 10;

// Plays audio once (exam) or with replay (practice); if the browser blocks it, offers a tap-to-replay
export const useItemAudio = (part, item, rate, enabled) => {
  const [phase, setPhase] = useState(enabled ? "listening" : "answer");
  const [grace, setGrace] = useState(GRACE);
  const runId = useRef(0);
  const play = useCallback(async () => {
    const id = ++runId.current;
    cancelSpeech();
    setPhase("listening");
    const ok = await playItemAudio(part.key, item, rate, () => id !== runId.current);
    if (id === runId.current) setPhase(ok ? "answer" : "failed");
  }, [part.key, item, rate]);
  const replayFromTap = useCallback(() => { unlockAudio(true); play(); }, [play]);
  useEffect(() => {
    if (enabled) play();
    return () => { runId.current++; cancelSpeech(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (phase !== "failed") return undefined;
    setGrace(GRACE);
    const end = Date.now() + GRACE * 1000;
    const t = setInterval(() => {
      const l = Math.max(0, Math.ceil((end - Date.now()) / 1000));
      setGrace(l);
      if (l <= 0) { clearInterval(t); runId.current++; setPhase("answer"); }
    }, 250);
    return () => clearInterval(t);
  }, [phase]);
  return { phase, play: replayFromTap, grace };
};

const scrollOnce = (el) => { if (el && !el.dataset.scrolled) { el.dataset.scrolled = "1"; el.scrollIntoView?.({ block: "center", behavior: "smooth" }); } };

export const AudioFailedPanel = ({ onReplay, grace }) => (
  <div ref={scrollOnce} data-testid="audio-failed-panel" className="flex flex-wrap items-center gap-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-5">
    <VolumeX className="h-6 w-6 shrink-0 text-amber-600" />
    <div className="flex-1 text-sm text-amber-900">
      <b>The audio prompt didn't play.</b>
      <p className="text-xs">Your browser blocked sound or no voice is available. Turn off silent mode, raise the volume, then tap Replay prompt. Response time starts in <span data-testid="audio-failed-grace" className="font-mono font-bold">{grace}s</span>.</p>
    </div>
    <button data-testid="audio-failed-replay-btn" onClick={onReplay} className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-bold text-slate-900 transition-colors hover:bg-amber-400"><Play className="h-4 w-4" />Replay prompt</button>
  </div>
);

export const ReplayButton = ({ onClick, disabled }) => (
  <button data-testid="audio-replay-btn" onClick={onClick} disabled={disabled} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-40">
    <RotateCcw className="h-3.5 w-3.5" /> Replay audio
  </button>
);

export const Prompt = ({ part, item, phase }) => {
  if (part.key === "A") return <p data-testid="read-aloud-passage" className="font-display text-xl leading-relaxed text-slate-800">{item.text}</p>;
  return (
    <div data-testid="audio-prompt-indicator" data-playing={phase === "listening"} className={`flex items-center gap-4 rounded-xl p-5 transition-colors ${phase === "listening" ? "bg-indigo-50 ring-2 ring-indigo-300" : "bg-slate-50"}`}>
      <Volume2 className={`h-6 w-6 text-indigo-600 ${phase === "listening" ? "animate-pulse" : "opacity-50"}`} />
      <Waveform active={phase === "listening"} />
      <span className={`text-sm ${phase === "listening" ? "font-semibold text-indigo-800" : "text-slate-500"}`}>{phase === "listening" ? "Playing prompt… listen carefully" : phase === "failed" ? "Prompt not heard yet" : "Audio finished"}</span>
    </div>
  );
};
